import { z } from 'zod';
import { cursorQuerySchema } from './core.js';
/* ------------------------------------------------------------------ */
/* Hằng số                                                             */
/* ------------------------------------------------------------------ */
/** 6 bước của bảng Kanban, theo đúng thứ tự cột. */
export const PRODUCTION_STAGES = [
    'idea',
    'script',
    'editing',
    'review',
    'scheduled',
    'published',
];
export const productionStageSchema = z.enum(PRODUCTION_STAGES);
export const PRODUCTION_STAGE_LABEL = {
    idea: 'Ý tưởng',
    script: 'Kịch bản',
    editing: 'Dựng video',
    review: 'Kiểm duyệt',
    scheduled: 'Đã lên lịch',
    published: 'Đã đăng',
};
/** Màu chấm của từng cột. */
export const PRODUCTION_STAGE_COLOR = {
    idea: '#98A2B3',
    script: '#6172F3',
    editing: '#F79009',
    review: '#B54708',
    scheduled: '#3538CD',
    published: '#17B26A',
};
export const PRODUCTION_TOOLS = [
    'research',
    'ai_script',
    'translate',
    'ffmpeg_pipeline',
    'veo3',
    'wan22',
    'review',
];
export const PRODUCTION_TOOL_LABEL = {
    research: 'Nghiên cứu',
    ai_script: 'Kịch bản AI',
    translate: 'Dịch',
    ffmpeg_pipeline: 'Pipeline FFmpeg',
    veo3: 'Veo 3',
    wan22: 'Wan 2.2',
    review: 'Kiểm duyệt',
};
/* ------------------------------------------------------------------ */
/* Mục sản xuất                                                        */
/* ------------------------------------------------------------------ */
export const productionMetadataSchema = z.object({
    title: z.string().optional(),
    description: z.string().optional(),
    tags: z.array(z.string()).optional(),
    chapters: z.array(z.object({ at: z.number().int().nonnegative(), label: z.string() })).optional(),
});
export const checklistEntrySchema = z.object({
    key: z.string(),
    label: z.string(),
    checked: z.boolean(),
    checkedBy: z.string().nullable(),
    checkedAt: z.iso.datetime().nullable(),
});
export const productionItemSchema = z.object({
    id: z.string(),
    channelId: z.string(),
    channelName: z.string(),
    channelColor: z.string(),
    title: z.string(),
    stage: productionStageSchema,
    assigneeId: z.string().nullable(),
    assigneeName: z.string().nullable(),
    dueDate: z.iso.datetime().nullable(),
    /// Số ngày trễ hạn, 0 khi chưa trễ.
    lateDays: z.number().int().nonnegative(),
    tool: z.string().nullable(),
    notes: z.string().nullable(),
    scriptText: z.string().nullable(),
    metadata: productionMetadataSchema.nullable(),
    thumbnailUrl: z.string().nullable(),
    videoFileUrl: z.string().nullable(),
    trackIds: z.array(z.string()),
    aiDisclosure: z.boolean(),
    youtubeVideoId: z.string().nullable(),
    position: z.number().int(),
    checklist: z.array(checklistEntrySchema),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
/** Bảng Kanban: mỗi cột một mảng thẻ. */
export const kanbanBoardSchema = z.object({
    columns: z.array(z.object({
        stage: productionStageSchema,
        label: z.string(),
        color: z.string(),
        count: z.number().int().nonnegative(),
        items: z.array(productionItemSchema),
    })),
});
/* ------------------------------------------------------------------ */
/* Input                                                               */
/* ------------------------------------------------------------------ */
export const listProductionQuerySchema = cursorQuerySchema.extend({
    channelId: z.string().optional(),
    assigneeId: z.string().optional(),
    stage: productionStageSchema.optional(),
    q: z.string().trim().optional(),
});
export const createProductionItemInputSchema = z.object({
    channelId: z.string().min(1),
    title: z.string().trim().min(2).max(200),
    stage: productionStageSchema.default('idea'),
    assigneeId: z.string().nullish(),
    dueDate: z.iso.datetime().nullish(),
    tool: z.string().trim().max(40).nullish(),
    notes: z.string().trim().max(5000).nullish(),
});
export const updateProductionItemInputSchema = createProductionItemInputSchema.partial().extend({
    scriptText: z.string().max(100_000).nullish(),
    metadata: productionMetadataSchema.nullish(),
    thumbnailUrl: z.string().max(500).nullish(),
    videoFileUrl: z.string().max(500).nullish(),
    trackIds: z.array(z.string()).max(20).optional(),
    aiDisclosure: z.boolean().optional(),
});
/** Đổi cột hoặc đổi vị trí trong cột. */
export const moveProductionItemInputSchema = z.object({
    stage: productionStageSchema,
    position: z.number().int().nonnegative().default(0),
});
/**
 * Lỗi 422 khi đổi stage vi phạm luật:
 * - sang `review` cần có file video và thumbnail;
 * - sang `scheduled` cần checklist đủ và mọi bài nhạc qua `checkTrackForChannel`.
 */
export const stageViolationSchema = z.object({
    code: z.enum(['missing_video', 'missing_thumbnail', 'checklist_incomplete', 'music_not_allowed']),
    message: z.string(),
});
export const setChecklistInputSchema = z.object({
    key: z.string().min(1),
    checked: z.boolean(),
});
export const ideaSchema = z.object({
    id: z.string(),
    channelId: z.string().nullable(),
    market: z.string(),
    topic: z.string(),
    keywords: z.array(z.string()),
    referenceUrls: z.array(z.string()),
    score: z.number().int(),
    createdAt: z.iso.datetime(),
});
export const createIdeaInputSchema = z.object({
    channelId: z.string().nullish(),
    market: z.string().trim().min(2).max(8),
    topic: z.string().trim().min(2).max(200),
    keywords: z.array(z.string().trim().min(1).max(40)).max(20).default([]),
    referenceUrls: z.array(z.string().trim().max(500)).max(10).default([]),
    score: z.number().int().min(0).max(100).default(0),
});
/** Kết quả `markScheduled` và `markPublished`. */
export const productionStageChangeSchema = z.object({
    id: z.string(),
    stage: productionStageSchema,
    youtubeVideoId: z.string().nullable(),
});
/** Sinh kịch bản bằng AI. Module 05 chỉ cài bản mock. */
export const generateScriptInputSchema = z.object({
    itemId: z.string().min(1),
    prompt: z.string().trim().max(2000).optional(),
});
export const generateScriptResultSchema = z.object({
    scriptText: z.string(),
    provider: z.string(),
});
//# sourceMappingURL=production.js.map